// components/user/lead/OrdersTab.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Empty, Spin, Card, Tag, Dropdown } from "antd";
import {
    EyeOutlined,
    CalendarOutlined,
    DeleteOutlined,
    RocketOutlined,
    MoreOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    WalletOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
    fetchServiceRequests,
    clearServiceDetails,
} from "../../../store/slices/serviceRequestSlice";
import ViewDetailsModal from "./ViewDetailsModal";
import RescheduleDrawer from "./RescheduleDrawer";
import { formatPrice } from "../../../utils/priceFormatter";
import CancelDrawer from "./CancelDrawer";
import { api } from "../../../services/api";
import toast from "react-hot-toast";

const OrdersTab = ({ isDarkMode }) => {
    const dispatch = useDispatch();
    const { orders, pagination, loading } = useSelector(
        (state) => state.serviceRequest,
    );

    // Modals/Drawers state
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [rescheduleDrawerVisible, setRescheduleDrawerVisible] =
        useState(false);
    const [cancelDrawerVisible, setCancelDrawerVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);

    // Fetch service requests
    const loadServiceRequests = (page = 1, pageSize = 10) => {
        dispatch(fetchServiceRequests({ page, perPage: pageSize }));
    };

    useEffect(() => {
        loadServiceRequests();
        return () => {
            dispatch(clearServiceDetails());
        };
    }, []);

    const handleTableChange = (paginationConfig) => {
        loadServiceRequests(
            paginationConfig.current,
            paginationConfig.pageSize,
        );
    };

    const handleViewDetails = (record) => {
        setSelectedOrder(record);
        setViewModalVisible(true);
    };

    const handleReschedule = (record) => {
        setSelectedOrder(record);
        setRescheduleDrawerVisible(true);
    };

    const handleCancel = (record) => {
        setSelectedOrder(record);
        setCancelDrawerVisible(true);
    };

    const handleActionSuccess = () => {
        loadServiceRequests(pagination.current, pagination.pageSize);
    };

    const handleDownloadReceipt = async (record) => {
        const serviceId = record.service_id;
        if (!serviceId || downloadingReceiptId !== null) return;

        setDownloadingReceiptId(serviceId);
        try {
            const { blob, filename } = await api.downloadServiceRequestReceipt(serviceId);
            if (!blob || blob.size === 0 || (blob.type && !blob.type.includes("pdf"))) {
                throw new Error("The receipt PDF could not be loaded.");
            }
            const safeName = filename && /^[a-zA-Z0-9_.-]+\.pdf$/i.test(filename)
                ? filename
                : `receipt-${serviceId}.pdf`;
            const url = URL.createObjectURL(blob);
            try {
                const link = document.createElement("a");
                link.href = url;
                link.download = safeName;
                document.body.appendChild(link);
                link.click();
                link.remove();
            } finally {
                window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
            toast.success("Receipt download started.");
        } catch (error) {
            toast.error(
                error?.response?.status === 404
                    ? "Receipt is not available for this service request yet."
                    : "Unable to download receipt. Please try again.",
            );
        } finally {
            setDownloadingReceiptId(null);
        }
    };

    const STATUS_CLASSES = {
        available: "bg-blue-100 text-blue-800",
        assigned: "bg-indigo-100 text-indigo-800",
        requested_to_cancel: "bg-yellow-100 text-yellow-800",
        cancelled: "bg-red-100 text-red-800",
        callback: "bg-purple-100 text-purple-800",
        re_scheduled: "bg-orange-100 text-orange-800",
        reached_doorstep: "bg-teal-100 text-teal-800",
        rejected: "bg-red-100 text-red-800",
        completed: "bg-green-100 text-green-800",
    };

    const getStatusTag = (status, statusLabel) => {
        const statusClass =
            STATUS_CLASSES[status] || "bg-gray-100 text-gray-800";

        return (
            <span
                className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${statusClass}`}
            >
                {statusLabel || status}
            </span>
        );
    };

    // Format time slot display
    const formatTimeSlot = (slot) => {
        if (!slot) return "Not scheduled";
        const [start, end] = slot.split("-");
        const formatTime = (time) => {
            const hour = parseInt(time.split(":")[0]);
            const minute = time.split(":")[1];
            const ampm = hour >= 12 ? "PM" : "AM";
            const hour12 = hour % 12 || 12;
            return `${hour12}:${minute} ${ampm}`;
        };
        return `${formatTime(start)} - ${formatTime(end)}`;
    };

    // Check if reschedule is allowed
    const canReschedule = (status) => {
        const terminalStatuses = ["completed", "failed"];
        return !terminalStatuses.includes(status);
    };

    // Check if cancel is allowed
    const canCancel = (status) => {
        const terminalStatuses = ["completed", "failed"];
        return !terminalStatuses.includes(status);
    };

    // Get action menu items
    const getActionMenuItems = (record) => {
        const items = [
            {
                key: "view",
                label: "View Details",
                icon: <EyeOutlined />,
                onClick: () => handleViewDetails(record),
            },
        ];

        if (record.status === "completed") {
            items.push({
                key: "receipt",
                label: "Download Receipt",
                icon: <DownloadOutlined />,
                disabled: downloadingReceiptId !== null,
                onClick: () => handleDownloadReceipt(record),
            });
        }

        if (canReschedule(record.status)) {
            items.push({
                key: "reschedule",
                label: "Reschedule",
                icon: <CalendarOutlined />,
                onClick: () => handleReschedule(record),
            });
        }

        if (canCancel(record.status)) {
            items.push({
                key: "cancel",
                label: "Cancel",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleCancel(record),
            });
        }

        return items;
    };

    const columns = [
        {
            title: "Service No",
            dataIndex: "service_no",
            key: "service_no",
            width: 120,
            render: (text) => (
                <span className="font-mono text-xs font-medium">{text}</span>
            ),
        },
        {
            title: "Product / City",
            dataIndex: "product_name",
            key: "product_name",
            width: 200,
            render: (text, record) => (
                <div>
                    <div className="text-xs font-medium">
                        {record.variation?.title || "N/A"}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <EnvironmentOutlined size={10} />
                            {record.address_details?.city_name || "N/A"}
                        </span>
                        {record.is_express && (
                            <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                                <RocketOutlined size={10} />
                                Express
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            width: 100,
            render: (_, record) => {
                const amount = record.latestEvaluation?.evaluated_amount || 0;
                return (
                    <div className="flex items-center gap-1">
                        <WalletOutlined className="text-xs text-gray-400" />
                        <span className="text-xs font-semibold">
                            {formatPrice(amount)}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) =>
                 getStatusTag(record.status, record.status_label),
        },
        {
            title: "Pickup Info",
            key: "pickup_info",
            width: 160,
            render: (_, record) => {
                if (record.is_express) {
                    return (
                        <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                                <RocketOutlined size={10} />
                                Express Pickup
                            </span>
                        </div>
                    );
                }
                return (
                    <div className="flex flex-col gap-1">
                        {record.scheduling?.spd && (
                            <span className="inline-flex items-center gap-1 text-xs">
                                <CalendarOutlined
                                    size={10}
                                    className="text-gray-400"
                                />
                                {dayjs(record.scheduling.spd).format(
                                    "DD MMM YYYY",
                                )}
                            </span>
                        )}
                        {record.scheduling?.slot && (
                            <span className="inline-flex items-center gap-1 text-xs">
                                <ClockCircleOutlined
                                    size={10}
                                    className="text-gray-400"
                                />
                                {formatTimeSlot(record.scheduling.slot)}
                            </span>
                        )}
                        {!record.scheduling?.spd &&
                            !record.scheduling?.slot && (
                                <span className="text-xs text-gray-400">
                                    Not scheduled
                                </span>
                            )}
                    </div>
                );
            },
        },
        {
            title: "",
            key: "action",
            width: 50,
            fixed: "right",
            align: "center",
            render: (_, record) => (
                <Dropdown
                    menu={{ items: getActionMenuItems(record) }}
                    trigger={["click"]}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        size="small"
                        icon={<MoreOutlined />}
                        className="flex items-center justify-center"
                        style={{ width: 32, height: 32 }}
                    />
                </Dropdown>
            ),
        },
    ];

    // Show loading state
    if (loading && orders.length === 0) {
        return (
            <div className="pt-2">
                <Card className="border-0 shadow-sm rounded-xl">
                    <div className="flex justify-center py-12">
                        <Spin size="large" />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-2">
            <Card className="border-0 shadow-sm rounded-xl">
                {orders.length === 0 ? (
                    <Empty
                        description="No service requests found"
                        className="py-12"
                    />
                ) : (
                    <>
                        <div className="mb-4">
                            <h3
                                className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                            >
                                My Service Requests
                            </h3>
                            <p className="mt-1 text-xs text-gray-500">
                                Total {pagination.total} requests
                            </p>
                        </div>
                        <Table
                            columns={columns}
                            dataSource={orders}
                            rowKey="service_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total) => (
                                    <span className="text-xs">
                                        Total {total} requests
                                    </span>
                                ),
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 850 }}
                            size="small"
                            loading={loading}
                        />
                    </>
                )}
            </Card>

            {/* View Details Modal */}
            <ViewDetailsModal
                visible={viewModalVisible}
                order={selectedOrder}
                onClose={() => {
                    setViewModalVisible(false);
                    setSelectedOrder(null);
                }}
                isDarkMode={isDarkMode}
            />

            {/* Reschedule Drawer */}
            <RescheduleDrawer
                visible={rescheduleDrawerVisible}
                order={selectedOrder}
                onClose={() => {
                    setRescheduleDrawerVisible(false);
                    setSelectedOrder(null);
                }}
                onSuccess={handleActionSuccess}
                isDarkMode={isDarkMode}
            />

            {/* Cancel Drawer */}
            <CancelDrawer
                visible={cancelDrawerVisible}
                order={selectedOrder}
                onClose={() => {
                    setCancelDrawerVisible(false);
                    setSelectedOrder(null);
                }}
                onSuccess={handleActionSuccess}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

export default OrdersTab;
